import React, { useEffect, useState } from 'react';
import authService from '../../services/authService';
import Toast from '../Toast';

const Section = ({ title, children }) => (
  <div className="dashboard-card" style={{ padding:8, marginBottom:12 }}>
    <h3 style={{ marginTop:0 }}>{title}</h3>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 }}>
      {children}
    </div>
  </div>
);

const Input = ({ label, ...props }) => (
  <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
    <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>{label}</span>
    <input 
      {...props}
      style={{
        padding: '10px 12px',
        border: '1.5px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        background: '#fff',
        width: '100%',
        boxSizing: 'border-box',
        ...(props.style || {})
      }}
      onFocus={(e)=>{
        e.target.style.borderColor = '#667eea';
        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
      }}
      onBlur={(e)=>{
        e.target.style.borderColor = '#e5e7eb';
        e.target.style.boxShadow = 'none';
      }}
    />
  </label>
);

const TextArea = ({ label, ...props }) => (
  <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
    <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>{label}</span>
    <textarea 
      rows={3}
      {...props}
      style={{
        padding: '10px 12px',
        border: '1.5px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        background: '#fff',
        width: '100%',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        ...(props.style || {})
      }}
      onFocus={(e)=>{
        e.target.style.borderColor = '#667eea';
        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
      }}
      onBlur={(e)=>{
        e.target.style.borderColor = '#e5e7eb';
        e.target.style.boxShadow = 'none';
      }}
    />
  </label>
);

const AdminRegistration = () => {
  const [toast, setToast] = useState({ show:false, type:'success', message:'' });
  const showToast = (message, type='success') => setToast({ show:true, type, message });
  const hideToast = () => setToast(prev => ({ ...prev, show:false }));

  const [org, setOrg] = useState({
    id:null, name:'', slug:'', ownerUserId:'',
    orgName:'', legalName:'', address1:'', address2:'', city:'', state:'', postalCode:'', country:'',
    contactName:'', contactEmail:'', contactPhone:'', notes:'',
    subscriptionStart:'', subscriptionEnd:'', subscriptionCadence:'', subscriptionInterval:'', subscriptionAmountUSD:'', negotiatedMarginPercent:'', isActive:true,
    stripeCustomerId:''
  });
  const [orgSlugManuallyEdited, setOrgSlugManuallyEdited] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [creatingStripeCustomer, setCreatingStripeCustomer] = useState(false);
  const [team, setTeam] = useState({
    id:null, name:'', slug:'', ownerUserId:'',
    teamName:'', orgId:'', address1:'', address2:'', city:'', state:'', postalCode:'', country:'',
    contactName:'', contactEmail:'', contactPhone:'', notes:'',
    subscriptionStart:'', subscriptionEnd:'', subscriptionCadence:'', subscriptionInterval:'', subscriptionAmountUSD:'', isActive:true
  });
  const [teamSlugManuallyEdited, setTeamSlugManuallyEdited] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [searchType, setSearchType] = useState('org'); // 'org' | 'team'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);

  const loadOrgById = async (id) => {
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/admin/registration/org/${id}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (!r.ok) throw new Error('Failed to load organization');
      const d = await r.json();
      setOrg({
        id: d.id || null, name: d.name || '', slug: d.slug || '', ownerUserId: d.ownerUserId || '',
        orgName: d.orgName || '', legalName: d.legalName || '', address1: d.address1 || '', address2: d.address2 || '', city: d.city || '', state: d.state || '', postalCode: d.postalCode || '', country: d.country || '',
        contactName: d.contactName || '', contactEmail: d.contactEmail || '', contactPhone: d.contactPhone || '', notes: d.notes || '',
        subscriptionStart: d.subscriptionStart ? new Date(d.subscriptionStart).toISOString().slice(0,16) : '',
        subscriptionEnd: d.subscriptionEnd ? new Date(d.subscriptionEnd).toISOString().slice(0,16) : '',
        subscriptionCadence: d.subscriptionCadence || '', subscriptionInterval: d.subscriptionInterval || '', subscriptionAmountUSD: d.subscriptionAmountUSD || '',
        negotiatedMarginPercent: d.negotiatedMarginPercent != null ? d.negotiatedMarginPercent : '',
        isActive: d.isActive !== false,
        stripeCustomerId: d.stripeCustomerId || ''
      });
      // Mark slug as manually edited since we loaded an existing org with a slug
      setOrgSlugManuallyEdited(!!d.slug);
      showToast(`Loaded Organization #${d.id}`, 'success');
    } catch (e) {
      showToast(e.message || 'Failed to load organization', 'error');
    }
  };

  const loadTeamById = async (id) => {
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/admin/registration/team/${id}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (!r.ok) throw new Error('Failed to load team');
      const d = await r.json();
      setTeam({
        id: d.id || null, name: d.name || '', slug: d.slug || '', ownerUserId: d.ownerUserId || '',
        teamName: d.teamName || '', orgId: d.orgId || '',
        address1: d.address1 || '', address2: d.address2 || '', city: d.city || '', state: d.state || '', postalCode: d.postalCode || '', country: d.country || '',
        contactName: d.contactName || '', contactEmail: d.contactEmail || '', contactPhone: d.contactPhone || '', notes: d.notes || '',
        subscriptionStart: d.subscriptionStart ? new Date(d.subscriptionStart).toISOString().slice(0,16) : '',
        subscriptionEnd: d.subscriptionEnd ? new Date(d.subscriptionEnd).toISOString().slice(0,16) : '',
        subscriptionCadence: d.subscriptionCadence || '', subscriptionInterval: d.subscriptionInterval || '', subscriptionAmountUSD: d.subscriptionAmountUSD || '', isActive: d.isActive !== false
      });
      // Mark slug as manually edited since we loaded an existing team with a slug
      setTeamSlugManuallyEdited(!!d.slug);
      showToast(`Loaded Team #${d.id}`, 'success');
    } catch (e) {
      showToast(e.message || 'Failed to load team', 'error');
    }
  };

  const doSearch = async () => {
    setSearchBusy(true);
    try {
      const t = authService.getToken();
      const url = searchType === 'org'
        ? `/api/admin/registration/orgs?q=${encodeURIComponent(searchTerm)}&page=1&pageSize=25`
        : `/api/admin/registration/teams?q=${encodeURIComponent(searchTerm)}&page=1&pageSize=25`;
      const r = await fetch(url, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (!r.ok) throw new Error('Search failed');
      const d = await r.json();
      setSearchResults(Array.isArray(d.items) ? d.items : []);
    } catch (e) {
      setSearchResults([]);
      showToast(e.message || 'Search failed', 'error');
    } finally {
      setSearchBusy(false);
    }
  };

  const slugify = (s) => {
    try {
      return (s || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-');
    } catch { return ''; }
  };

  // Auto-fill org slug from name (only if not manually edited)
  // Always update slug to mirror the name, including when name is cleared
  useEffect(() => {
    if (!orgSlugManuallyEdited) {
      setOrg(o => ({ ...o, slug: slugify(o.name) }));
    }
  }, [org.name, orgSlugManuallyEdited]);
  
  // Auto-fill team slug from name (only if not manually edited)
  // Always update slug to mirror the name, including when name is cleared
  useEffect(() => {
    if (!teamSlugManuallyEdited) {
      setTeam(t => ({ ...t, slug: slugify(t.name) }));
    }
  }, [team.name, teamSlugManuallyEdited]);

  const randomId = () => {
    try { return crypto.randomUUID(); } catch { /* fallback */ }
    const rnd = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${rnd()}${rnd()}-${rnd()}-${rnd()}-${rnd()}-${rnd()}${rnd()}${rnd()}`;
  };

  useEffect(() => {
    // Populate orgs for Team dropdown (best-effort)
    (async () => {
      try {
        const t = authService.getToken();
        const r = await fetch('/api/admin/teams', { headers: t ? { Authorization:`Bearer ${t}` } : {} });
        // If we don't have a listing endpoint, skip quietly
      } catch {}
      try {
        const t = authService.getToken();
        const r2 = await fetch('/api/admin/orgs', { headers: t ? { Authorization:`Bearer ${t}` } : {} });
        // If endpoint exists and returns list: setOrgs(await r2.json());
      } catch {}
    })();
  }, []);

  // PHASE 5: Create Stripe customer for organization
  const createStripeCustomer = async () => {
    if (!org.id) {
      showToast('Please save the organization first before creating a Stripe customer', 'error');
      return;
    }

    setCreatingStripeCustomer(true);
    try {
      const t = authService.getToken();
      const resp = await fetch(`/api/admin/registration/org/${org.id}/create-stripe-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({
          email: org.contactEmail || '',
          name: org.name || org.orgName || ''
        })
      });
      const res = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof res === 'string' ? res : (res.message || 'Failed to create Stripe customer'));
      
      setOrg(o => ({ ...o, stripeCustomerId: res.stripeCustomerId }));
      setShowStripeModal(false);
      showToast(`Stripe customer created: ${res.stripeCustomerId}`, 'success');
    } catch (e) {
      showToast(e.message || 'Failed to create Stripe customer', 'error');
    } finally {
      setCreatingStripeCustomer(false);
    }
  };

  const saveOrg = async () => {
    if (!org.name?.trim()) {
      showToast('Organization name is required', 'error');
      return;
    }
    
    try {
      // PHASE 5: Validate dates
      if (org.subscriptionStart && org.subscriptionEnd) {
        const startDate = new Date(org.subscriptionStart);
        const endDate = new Date(org.subscriptionEnd);
        if (endDate <= startDate) {
          showToast('Subscription end date must be after start date', 'error');
          return;
        }
        const now = new Date();
        if (startDate < now) {
          showToast('Subscription start date must be in the future', 'error');
          return;
        }
        if (endDate < now) {
          showToast('Subscription end date must be in the future', 'error');
          return;
        }
      }

      setSavingOrg(true);
      const t = authService.getToken();
      let baseSlug = org.slug?.trim() || slugify(org.name);
      let attempt = 0;
      const maxAttempts = 100;
      
      while (attempt < maxAttempts) {
        const currentSlug = attempt === 0 ? baseSlug : `${baseSlug}-${String(attempt).padStart(2, '0')}`;
        const body = {
          id: org.id || null, name: org.name || null, slug: currentSlug, ownerUserId: org.ownerUserId || null,
          orgName: org.orgName || null, legalName: org.legalName || null, address1: org.address1 || null, address2: org.address2 || null,
          city: org.city || null, state: org.state || null, postalCode: org.postalCode || null, country: org.country || null,
          contactName: org.contactName || null, contactEmail: org.contactEmail || null, contactPhone: org.contactPhone || null,
          notes: org.notes || null,
          subscriptionStart: org.subscriptionStart ? new Date(org.subscriptionStart).toISOString() : null,
          subscriptionEnd: org.subscriptionEnd ? new Date(org.subscriptionEnd).toISOString() : null,
          subscriptionCadence: org.subscriptionCadence || null, subscriptionInterval: org.subscriptionInterval ? Number(org.subscriptionInterval) : null,
          subscriptionAmountUSD: org.subscriptionAmountUSD ? Number(org.subscriptionAmountUSD) : null,
          negotiatedMarginPercent: org.negotiatedMarginPercent !== '' ? Number(org.negotiatedMarginPercent) : null,
          isActive: !!org.isActive,
          stripeCustomerId: org.stripeCustomerId || null
        };
        const resp = await fetch('/api/admin/registration/org', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) },
          body: JSON.stringify(body)
        });
        
        if (resp.ok) {
          const res = await resp.json().catch(()=>({}));
          if (res?.id) {
            setOrg(o => ({ ...o, id: res.id, slug: currentSlug }));
            // PHASE 5: Show Stripe modal if organization has basic info but no Stripe customer
            if (!org.stripeCustomerId && (org.name || org.orgName) && org.contactEmail) {
              setShowStripeModal(true);
            }
          }
          showToast(`Organization saved. ID: ${res?.id ?? '(unknown)'}, Slug: ${currentSlug}`, 'success');
          return;
        }
        
        const res = await resp.json().catch(()=>({}));
        const errMsg = typeof res === 'string' ? res : (res.message || '');
        
        // Check if error is due to duplicate slug
        if (errMsg.toLowerCase().includes('slug') && (errMsg.toLowerCase().includes('exists') || errMsg.toLowerCase().includes('duplicate'))) {
          attempt++;
          continue;
        }
        
        // Other error - break and show message
        throw new Error(errMsg || 'Failed to save organization');
      }
      
      showToast('Could not generate unique slug after 100 attempts', 'error');
    } catch (e) {
      showToast(e.message || 'Failed to save organization', 'error');
    } finally {
      setSavingOrg(false);
    }
  };

  const saveTeam = async () => {
    if (!team.name?.trim()) {
      showToast('Team name is required', 'error');
      return;
    }
    
    try {
      setSavingTeam(true);
      const t = authService.getToken();
      let baseSlug = team.slug?.trim() || slugify(team.name);
      let attempt = 0;
      const maxAttempts = 100;
      
      while (attempt < maxAttempts) {
        const currentSlug = attempt === 0 ? baseSlug : `${baseSlug}-${String(attempt).padStart(2, '0')}`;
        const body = {
          id: team.id || null, name: team.name || null, slug: currentSlug, ownerUserId: team.ownerUserId || null,
          teamName: team.teamName || null, orgId: team.orgId ? Number(team.orgId) : null,
          address1: team.address1 || null, address2: team.address2 || null, city: team.city || null, state: team.state || null, postalCode: team.postalCode || null, country: team.country || null,
          contactName: team.contactName || null, contactEmail: team.contactEmail || null, contactPhone: team.contactPhone || null,
          notes: team.notes || null,
          subscriptionStart: team.subscriptionStart ? new Date(team.subscriptionStart).toISOString() : null,
          subscriptionEnd: team.subscriptionEnd ? new Date(team.subscriptionEnd).toISOString() : null,
          subscriptionCadence: team.subscriptionCadence || null, subscriptionInterval: team.subscriptionInterval ? Number(team.subscriptionInterval) : null,
          subscriptionAmountUSD: team.subscriptionAmountUSD ? Number(team.subscriptionAmountUSD) : null, isActive: !!team.isActive
        };
        const resp = await fetch('/api/admin/registration/team', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) },
          body: JSON.stringify(body)
        });
        
        if (resp.ok) {
          const res = await resp.json().catch(()=>({}));
          if (res?.id) setTeam(o => ({ ...o, id: res.id, slug: currentSlug }));
          showToast(`Team saved. ID: ${res?.id ?? '(unknown)'}, Slug: ${currentSlug}`, 'success');
          return;
        }
        
        const res = await resp.json().catch(()=>({}));
        const errMsg = typeof res === 'string' ? res : (res.message || '');
        
        // Check if error is due to duplicate slug
        if (errMsg.toLowerCase().includes('slug') && (errMsg.toLowerCase().includes('exists') || errMsg.toLowerCase().includes('duplicate'))) {
          attempt++;
          continue;
        }
        
        // Other error - break and show message
        throw new Error(errMsg || 'Failed to save team');
      }
      
      showToast('Could not generate unique slug after 100 attempts', 'error');
    } catch (e) {
      showToast(e.message || 'Failed to save team', 'error');
    } finally {
      setSavingTeam(false);
    }
  };

  return (
    <div className="admin-section">
      <h2 className="admin-header">Team/Org Registration</h2>

      <div className="dashboard-card" style={{ padding:8, marginBottom:12 }}>
        <h3 style={{ marginTop:0 }}>Search</h3>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Type</span>
            <select 
              value={searchType} 
              onChange={e=>setSearchType(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s ease',
                background: '#fff',
                cursor: 'pointer',
                minWidth: '140px'
              }}
              onFocus={(e)=>{
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e)=>{
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="org">Organization</option>
              <option value="team">Team</option>
            </select>
          </label>
          <input 
            style={{
              flex:'1 1 240px',
              padding: '10px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              boxSizing: 'border-box'
            }}
            placeholder="Search by Name or Slug" 
            value={searchTerm} 
            onChange={e=>setSearchTerm(e.target.value)} 
            onKeyDown={e=>{ if (e.key==='Enter') doSearch(); }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
          <button 
            onClick={doSearch} 
            disabled={searchBusy}
            style={{
              padding: '10px 20px',
              background: searchBusy ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: searchBusy ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: searchBusy ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
              opacity: searchBusy ? 0.7 : 1
            }}
            onMouseEnter={(e)=>{
              if (!searchBusy) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }
            }}
            onMouseLeave={(e)=>{
              if (!searchBusy) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
              }
            }}
          >
            {searchBusy ? 'Searching…' : 'Search'}
          </button>
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop:12, overflowX:'auto' }}>
            <table className="admin-table">
              <thead>
                <tr><th>ID</th><th>Name</th><th>Slug</th><th>Owner</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {searchResults.map(r => (
                  <tr key={`${searchType}-${r.id}`}>
                    <td>{r.id}</td>
                    <td>{r.name}</td>
                    <td>{r.slug}</td>
                    <td>{r.ownerUserId || '-'}</td>
                    <td>
                      {searchType === 'org' ? (
                        <button 
                          onClick={()=>loadOrgById(r.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#fff',
                            color: '#667eea',
                            border: '1.5px solid #667eea',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e)=>{
                            e.target.style.background = '#667eea';
                            e.target.style.color = '#fff';
                          }}
                          onMouseLeave={(e)=>{
                            e.target.style.background = '#fff';
                            e.target.style.color = '#667eea';
                          }}
                        >
                          Load Org
                        </button>
                      ) : (
                        <button 
                          onClick={()=>loadTeamById(r.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#fff',
                            color: '#667eea',
                            border: '1.5px solid #667eea',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e)=>{
                            e.target.style.background = '#667eea';
                            e.target.style.color = '#fff';
                          }}
                          onMouseLeave={(e)=>{
                            e.target.style.background = '#fff';
                            e.target.style.color = '#667eea';
                          }}
                        >
                          Load Team
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Section title="Organization Info">
        <Input label="Name" value={org.name} onChange={e=>setOrg({...org, name:e.target.value})} />
        <Input label="Slug" value={org.slug} onChange={e=>{setOrg({...org, slug:e.target.value}); setOrgSlugManuallyEdited(true);}} placeholder="auto-generated from name" />
        <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Owner User Id</span>
          <input 
            value={org.ownerUserId} 
            onChange={e=>setOrg({...org, ownerUserId:e.target.value})}
            style={{
              padding: '10px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              width: '100%',
              boxSizing: 'border-box'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </label>
        <Input label="Org Name" value={org.orgName} onChange={e=>setOrg({...org, orgName:e.target.value})} />
        <Input label="Legal Name" value={org.legalName} onChange={e=>setOrg({...org, legalName:e.target.value})} />
        <TextArea label="Notes" value={org.notes} onChange={e=>setOrg({...org, notes:e.target.value})} />
      </Section>

      <Section title="Organization Address">
        <Input label="Address 1" value={org.address1} onChange={e=>setOrg({...org, address1:e.target.value})} />
        <Input label="Address 2" value={org.address2} onChange={e=>setOrg({...org, address2:e.target.value})} />
        <Input label="City" value={org.city} onChange={e=>setOrg({...org, city:e.target.value})} />
        <Input label="State" value={org.state} onChange={e=>setOrg({...org, state:e.target.value})} />
        <Input label="Postal Code" value={org.postalCode} onChange={e=>setOrg({...org, postalCode:e.target.value})} />
        <Input label="Country" value={org.country} onChange={e=>setOrg({...org, country:e.target.value})} />
      </Section>

      <Section title="Organization Contacts">
        <Input label="Contact Name" value={org.contactName} onChange={e=>setOrg({...org, contactName:e.target.value})} />
        <Input label="Contact Email" value={org.contactEmail} onChange={e=>setOrg({...org, contactEmail:e.target.value})} />
        <Input label="Contact Phone" value={org.contactPhone} onChange={e=>setOrg({...org, contactPhone:e.target.value})} />
      </Section>

      <Section title="Organization Subscription (USD)">
        <Input label="Start (local)" type="datetime-local" value={org.subscriptionStart} onChange={e=>setOrg({...org, subscriptionStart:e.target.value})} />
        <Input label="End (local)" type="datetime-local" value={org.subscriptionEnd} onChange={e=>setOrg({...org, subscriptionEnd:e.target.value})} />
        <Input label="Cadence (free text)" value={org.subscriptionCadence} onChange={e=>setOrg({...org, subscriptionCadence:e.target.value})} />
        <Input label="Interval (int)" type="number" value={org.subscriptionInterval} onChange={e=>setOrg({...org, subscriptionInterval:e.target.value})} />
        <Input label="Amount USD" type="number" step="0.0001" value={org.subscriptionAmountUSD} onChange={e=>setOrg({...org, subscriptionAmountUSD:e.target.value})} />
        <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Negotiated Margin %</span>
          <input 
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="60 (default)"
            value={org.negotiatedMarginPercent}
            onChange={e=>setOrg({...org, negotiatedMarginPercent:e.target.value})}
            style={{
              padding: '10px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              width: '100%',
              boxSizing: 'border-box'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
          <span style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>
            Controls credit multiplier. 60% = 1x, 50% = 1.25x (25% bonus), 40% = 1.5x (50% bonus)
          </span>
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
          <input type="checkbox" checked={!!org.isActive} onChange={e=>setOrg({...org, isActive:e.target.checked})} />
          <span>Active</span>
        </label>
      </Section>

      {/* PHASE 5: Stripe Integration Section */}
      {((org.name || org.orgName) && org.contactEmail) && (
        <Section title="Stripe Integration">
          {org.stripeCustomerId ? (
            <div style={{ gridColumn: '1 / -1', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
              <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Stripe Customer ID</span>
                <input 
                  value={org.stripeCustomerId} 
                  readOnly
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #bfdbfe',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#fff',
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'monospace'
                  }}
                />
              </label>
            </div>
          ) : (
            <div style={{ gridColumn: '1 / -1', padding: '12px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#92400e' }}>
                No Stripe customer account. Create one now to enable billing integration.
              </p>
              <button 
                onClick={() => setShowStripeModal(true)}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }}
              >
                Create Stripe Account
              </button>
            </div>
          )}
        </Section>
      )}

      {/* PHASE 5: Stripe Customer Creation Modal */}
      {showStripeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => !creatingStripeCustomer && setShowStripeModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Create Stripe Account for Billing?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
              Create a Stripe customer account now? Credits won't be allocated until payment is processed.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowStripeModal(false)}
                disabled={creatingStripeCustomer}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  color: '#374151',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: creatingStripeCustomer ? 'not-allowed' : 'pointer',
                  opacity: creatingStripeCustomer ? 0.6 : 1
                }}
              >
                Later
              </button>
              <button
                onClick={createStripeCustomer}
                disabled={creatingStripeCustomer || !org.id}
                style={{
                  padding: '10px 20px',
                  background: creatingStripeCustomer || !org.id ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: creatingStripeCustomer || !org.id ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {creatingStripeCustomer ? 'Creating...' : 'Open Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-controls" style={{ justifyContent:'flex-end', marginBottom:24, display:'flex' }}>
        <button 
          onClick={saveOrg}
          disabled={savingOrg}
          style={{
            padding: '12px 24px',
            background: savingOrg ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: savingOrg ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: savingOrg ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
            opacity: savingOrg ? 0.7 : 1
          }}
          onMouseEnter={(e)=>{
            if (!savingOrg) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }
          }}
          onMouseLeave={(e)=>{
            if (!savingOrg) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
            }
          }}
        >
          {savingOrg ? 'Saving…' : 'Save Organization'}
        </button>
      </div>

      <Section title="Team Info">
        <Input label="Name" value={team.name} onChange={e=>setTeam({...team, name:e.target.value})} />
        <Input label="Slug" value={team.slug} onChange={e=>{setTeam({...team, slug:e.target.value}); setTeamSlugManuallyEdited(true);}} placeholder="auto-generated from name" />
        <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Owner User Id</span>
          <input 
            value={team.ownerUserId} 
            onChange={e=>setTeam({...team, ownerUserId:e.target.value})}
            style={{
              padding: '10px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              width: '100%',
              boxSizing: 'border-box'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </label>
        <Input label="Team Name" value={team.teamName} onChange={e=>setTeam({...team, teamName:e.target.value})} />
        <Input label="Org Id" value={team.orgId} onChange={e=>setTeam({...team, orgId:e.target.value})} />
        <TextArea label="Notes" value={team.notes} onChange={e=>setTeam({...team, notes:e.target.value})} />
      </Section>

      <Section title="Team Address">
        <Input label="Address 1" value={team.address1} onChange={e=>setTeam({...team, address1:e.target.value})} />
        <Input label="Address 2" value={team.address2} onChange={e=>setTeam({...team, address2:e.target.value})} />
        <Input label="City" value={team.city} onChange={e=>setTeam({...team, city:e.target.value})} />
        <Input label="State" value={team.state} onChange={e=>setTeam({...team, state:e.target.value})} />
        <Input label="Postal Code" value={team.postalCode} onChange={e=>setTeam({...team, postalCode:e.target.value})} />
        <Input label="Country" value={team.country} onChange={e=>setTeam({...team, country:e.target.value})} />
      </Section>

      <Section title="Team Contacts">
        <Input label="Contact Name" value={team.contactName} onChange={e=>setTeam({...team, contactName:e.target.value})} />
        <Input label="Contact Email" value={team.contactEmail} onChange={e=>setTeam({...team, contactEmail:e.target.value})} />
        <Input label="Contact Phone" value={team.contactPhone} onChange={e=>setTeam({...team, contactPhone:e.target.value})} />
      </Section>

      <Section title="Team Subscription (USD)">
        <Input label="Start (local)" type="datetime-local" value={team.subscriptionStart} onChange={e=>setTeam({...team, subscriptionStart:e.target.value})} />
        <Input label="End (local)" type="datetime-local" value={team.subscriptionEnd} onChange={e=>setTeam({...team, subscriptionEnd:e.target.value})} />
        <Input label="Cadence (free text)" value={team.subscriptionCadence} onChange={e=>setTeam({...team, subscriptionCadence:e.target.value})} />
        <Input label="Interval (int)" type="number" value={team.subscriptionInterval} onChange={e=>setTeam({...team, subscriptionInterval:e.target.value})} />
        <Input label="Amount USD" type="number" step="0.0001" value={team.subscriptionAmountUSD} onChange={e=>setTeam({...team, subscriptionAmountUSD:e.target.value})} />
        <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
          <input type="checkbox" checked={!!team.isActive} onChange={e=>setTeam({...team, isActive:e.target.checked})} />
          <span>Active</span>
        </label>
      </Section>

      <div className="admin-controls" style={{ justifyContent:'flex-end', display:'flex' }}>
        <button 
          onClick={saveTeam}
          disabled={savingTeam}
          style={{
            padding: '12px 24px',
            background: savingTeam ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: savingTeam ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: savingTeam ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
            opacity: savingTeam ? 0.7 : 1
          }}
          onMouseEnter={(e)=>{
            if (!savingTeam) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }
          }}
          onMouseLeave={(e)=>{
            if (!savingTeam) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
            }
          }}
        >
          {savingTeam ? 'Saving…' : 'Save Team'}
        </button>
      </div>

      <Toast show={toast.show} type={toast.type} message={toast.message} onClose={hideToast} />
    </div>
  );
};

export default AdminRegistration;

